'use client';

/**
 * useCheckoutPayment Hook
 *
 * Payment state management for checkout.
 * Actual payment methods are handled by the payments registry.
 * This hook manages the payment flow state.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import type { SplitPaymentConfig } from '@/lib/checkout/payments';
import { checkoutActions, checkoutState } from '@/states/checkout';

/**
 * Payment flow status
 */
export type PaymentStatus =
  | 'idle'
  | 'selecting_method'
  | 'processing'
  | 'awaiting_action' // 3DS, redirect, etc.
  | 'polling' // Async financing
  | 'completed'
  | 'failed';

/**
 * Payment operation result
 */
export interface PaymentOperationResult {
  success: boolean;
  requiresAction: boolean;
  action?: {
    type: 'redirect' | '3ds' | 'polling';
    url?: string;
    data?: Record<string, unknown>;
  };
  errors: Array<{ code: string; message: string }>;
}

/**
 * Hook options
 */
export interface UseCheckoutPaymentOptions {
  locale?: string;
}

/**
 * Payment state hook for checkout flow
 */
export function useCheckoutPayment(_options: UseCheckoutPaymentOptions = {}) {
  const snapshot = useSnapshot(checkoutState);

  // Local payment state
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [selectedMethodType, setSelectedMethodType] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  // Split payment state
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [payments, setPayments] = useState<
    Array<{
      method: string;
      amount: number;
      transactionId?: string;
    }>
  >([]);

  /**
   * Calculate split payment config
   */
  const splitPaymentConfig = useMemo((): SplitPaymentConfig | null => {
    if (!isSplitPayment) return null;

    const totalAmount = snapshot.checkout?.totalPrice?.gross?.amount || 0;
    const paidAmount = payments.reduce((sum, p) => sum + p.amount, 0);
    const remainingAmount = totalAmount - paidAmount;
    const currency = snapshot.checkout?.totalPrice?.gross?.currency || 'USD';

    return {
      maxPayments: 4, // Default, should come from config
      minAmount: 1, // Default, should come from config
      maxAmount: remainingAmount, // Use remaining as max for next payment
      paidMoney: paidAmount > 0 ? { amount: paidAmount, currency } : undefined,
    };
  }, [
    isSplitPayment,
    payments,
    snapshot.checkout?.totalPrice?.gross?.amount,
    snapshot.checkout?.totalPrice?.gross?.currency,
  ]);

  /**
   * Total amount for payment
   */
  const totalAmount = useMemo((): number => {
    const total = snapshot.checkout?.totalPrice?.gross?.amount || 0;
    if (splitPaymentConfig) {
      // Use maxAmount which represents remaining amount to be paid
      return splitPaymentConfig.maxAmount;
    }
    return total;
  }, [snapshot.checkout?.totalPrice?.gross?.amount, splitPaymentConfig]);

  /**
   * Currency code
   */
  const currency = snapshot.checkout?.totalPrice?.gross?.currency || 'USD';

  /**
   * Select payment method
   */
  const selectMethod = useCallback((methodType: string) => {
    setSelectedMethodType(methodType);
    setPaymentData({});
    setError(null);
    setStatus('selecting_method');
  }, []);

  /**
   * Update payment data (from payment form)
   */
  const updatePaymentData = useCallback((data: Partial<Record<string, unknown>>) => {
    setPaymentData((prev) => ({ ...prev, ...data }));
  }, []);

  /**
   * Clear payment selection
   */
  const clearSelection = useCallback(() => {
    setSelectedMethodType(null);
    setPaymentData({});
    setError(null);
    setStatus('idle');
  }, []);

  /**
   * Start split payment mode
   */
  const startSplitPayment = useCallback(() => {
    setIsSplitPayment(true);
    setPayments([]);
  }, []);

  /**
   * Add completed payment to split
   */
  const addSplitPayment = useCallback(
    (payment: { method: string; amount: number; transactionId?: string }) => {
      setPayments((prev) => [...prev, payment]);
      clearSelection();
    },
    [clearSelection]
  );

  /**
   * Cancel split payment mode
   */
  const cancelSplitPayment = useCallback(() => {
    setIsSplitPayment(false);
    setPayments([]);
    clearSelection();
  }, [clearSelection]);

  /**
   * Set payment status
   */
  const updateStatus = useCallback((newStatus: PaymentStatus) => {
    setStatus(newStatus);
    if (newStatus === 'failed') {
      checkoutActions.setProcessing(false);
    }
  }, []);

  /**
   * Set payment error
   */
  const setPaymentError = useCallback((message: string) => {
    setError(message);
    setStatus('failed');
    checkoutActions.setProcessing(false);
  }, []);

  /**
   * Check if ready to submit payment
   */
  const isReadyToSubmit = useMemo((): boolean => {
    if (!selectedMethodType) return false;
    if (status !== 'selecting_method') return false;
    if (totalAmount <= 0) return false;

    // Additional validation could check paymentData completeness
    return true;
  }, [selectedMethodType, status, totalAmount]);

  /**
   * Check if payment is complete
   */
  const isPaymentComplete = useMemo((): boolean => {
    if (!isSplitPayment) {
      return status === 'completed';
    }

    // For split payments, check if maxAmount (remaining) is 0
    return splitPaymentConfig?.maxAmount === 0;
  }, [isSplitPayment, status, splitPaymentConfig?.maxAmount]);

  return {
    // State
    status,
    selectedMethodType,
    paymentData,
    error,
    totalAmount,
    currency,

    // Split payment state
    isSplitPayment,
    splitPaymentConfig,
    payments,

    // Computed
    isReadyToSubmit,
    isPaymentComplete,
    isProcessing: status === 'processing' || status === 'polling',

    // Method selection
    selectMethod,
    updatePaymentData,
    clearSelection,

    // Split payment
    startSplitPayment,
    addSplitPayment,
    cancelSplitPayment,

    // Status management
    updateStatus,
    setPaymentError,
  };
}
